import BTbotbutton from '@common-components/bt-chatbot/bot-components/bt-bot-button';
import RowStack from '@common-components/common/row-stack/RowStack';
import { WhatsappIdCell } from '@common-components/tasks/columns-cells/whatsapp-id-cell/whatsapp-id-cell';
import { SearchBox } from '@common-components/tasks/person-picker/search-box';
import { getNodeDataByNodeId } from '@common-reducers/ChatbotSelectors';
import { changeNodeDataThunk } from '@common-reducers/ChatbotThunks';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { TriggerSource, TriggerType, TriggerSourceData, getAvailableTriggerSources, UserRole } from '@common/types/trigger.types';
import WaContactLazyLoadingVirtuoso from '@extension/context/content/components/add-scheduled-message-button/wa-contact-lazy-loading-virtuoso';
import { getChatsAndContactsThunk } from '@extension/context/content/thunks/WhatsappThunks';
import { Add, Close } from '@mui/icons-material';
import { Checkbox, Divider, FormControlLabel, IconButton, Radio, RadioGroup, Stack, Typography, Box, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import classes from "./trigger-node.module.scss";
import { SaveButton } from '../../bot-components/save-button';
import { TriggerSourceConfig } from '@common/components/TriggerSourceConfig';
import { selectUserObject } from '@/common/reducers/UserSelectors';

interface Contact {
    id: {
        _serialized: string;
    };
}

interface TriggerState {
    type: 'trigger';
    triggerType: TriggerType;
    contacts: string[]; // Array of contact IDs
    allowedTriggerSources?: TriggerSource[]; // Array of allowed trigger sources
    triggerSourceData?: Partial<Record<TriggerSource, TriggerSourceData>>; // Source-specific configuration data
}

interface Props {
    onClose: () => void;
    id?: string;
}

export default function AddTriggerDialog({ onClose, id }: Props) {
    const dispatch = useAppDispatch();

    const data = useAppSelector(state => getNodeDataByNodeId(state, id)) as TriggerState;

    // Get user role directly from the state slice
    // Adjust 'UserReducer' and 'role' if your state structure is different
    const userRole = useAppSelector(state => selectUserObject(state).role); 
    
    // Get available trigger sources based on role
    const availableTriggerSources = getAvailableTriggerSources(userRole);

    // Convert legacy 'twilio webhook' to 'blueticks assistant' for backwards compatibility
    const normalizeSourcesForBackwardCompatibility = (sources?: TriggerSource[]): TriggerSource[] => {
        if (!sources?.length) {
            return [];
        }

        return sources.map(source =>
            source === 'twilio webhook' as unknown as TriggerSource ? 'blueticks assistant' : source
        );
    };

    // Convert a string with spaces to camelCase
    // e.g., 'instagram page' becomes 'instagramPage'
    const toCamelCase = (str: string): string => {
        return str.replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
            return index === 0 ? word.toLowerCase() : word.toUpperCase();
        }).replace(/\s+/g, '');
    };

    const [localTriggerState, setLocalTriggerState] = useState<TriggerState>(() => {
        // If we have existing data, use it with normalized sources
        if (data) {
            const normalizedSources = normalizeSourcesForBackwardCompatibility(data?.allowedTriggerSources);
            
            // Handle triggerSourceData normalization
            let normalizedSourceData = data.triggerSourceData ? { ...data.triggerSourceData } : {};

            // Convert keys from multiple possible formats to camelCase
            // This handles backward compatibility for existing data
            const possibleKeys = ['instagram page', 'instagram'];
            possibleKeys.forEach(key => {
                if (normalizedSourceData[key] && !normalizedSourceData[toCamelCase(key)]) {
                    normalizedSourceData[toCamelCase(key)] = normalizedSourceData[key];
                    delete normalizedSourceData[key];
                }
            });
            
            return {
                ...data,
                allowedTriggerSources: normalizedSources,
                triggerSourceData: normalizedSourceData
            };
        }

        // For new trigger nodes, default to the first *available* trigger source for the user's role
        const firstAvailableSource = availableTriggerSources.length > 0 ? availableTriggerSources[0] : null;
        return {
            type: 'trigger',
            triggerType: 'blockList',
            contacts: [],
            allowedTriggerSources: firstAvailableSource ? [firstAvailableSource.value] : [],
            triggerSourceData: {}
        };
    });

    const [searchTerm, setSearchTerm] = useState('');

    const allowToAdd = /^[0-9@+]+$/.test(searchTerm);

    const handleTriggerTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalTriggerState(prev => ({
            ...prev,
            triggerType: event.target.value as TriggerType
        }));
    };

    const handleTriggerSourceChange = (source: TriggerSource) => {
        setLocalTriggerState(prev => {
            const currentSources = prev?.allowedTriggerSources || [];
            const newSources = currentSources.includes(source)
                ? currentSources.filter(s => s !== source) // Remove if already exists
                : [...currentSources, source]; // Add if doesn't exist

            return {
                ...prev,
                allowedTriggerSources: newSources
            };
        });
    };

    const handleUpdateSourceData = (source: TriggerSource, data: TriggerSourceData) => {
        setLocalTriggerState(prev => {
            // Get existing source data or initialize empty object
            const sourceData = prev.triggerSourceData || {};
            
            // Convert any source name to camelCase consistently
            const storageKey = toCamelCase(source);
            
            return {
                ...prev,
                triggerSourceData: {
                    ...sourceData,
                    [storageKey]: data
                }
            };
        });
    };

    const isTriggerSourceChecked = (source: TriggerSource): boolean => {
        return (localTriggerState?.allowedTriggerSources || []).includes(source);
    };

    const setPhoneNumberAsContact = (phoneNumber: string) => {
        if (parseInt(phoneNumber)) {
            const contact: Contact = { id: { _serialized: `${phoneNumber}@c.us` } };
            handleContactSelect(contact);
        }
    };

    const handleContactSelect = (contact: Contact | null) => {
        if (contact?.id?._serialized) {
            setLocalTriggerState(prev => ({
                ...prev,
                contacts: [...(prev.contacts || []), contact.id._serialized]
            }));
        }
        setSearchTerm('');
    };

    const handleRemoveContact = (contactId: string) => {
        setLocalTriggerState(prev => ({
            ...prev,
            contacts: (prev.contacts || []).filter(id => id !== contactId)
        }));
    };

    const saveTriggerData = async () => {
        await dispatch(changeNodeDataThunk({
            id,
            dataToPatch: localTriggerState
        }));

        return true;
    };

    // Load contacts when component mounts
    useEffect(() => {
        dispatch(getChatsAndContactsThunk({ shouldForceUpdate: true }));
    }, [dispatch]);

    // This function remains necessary because the child calls the callback 
    // with a partial update, and we need to merge it with the previous state.
    const handleUpdateTriggerData = (partialData: Partial<TriggerState>) => {
        setLocalTriggerState(prev => ({
            ...prev,
            ...partialData,
            // Ensure nested objects like triggerSourceData are merged correctly if needed
            triggerSourceData: {
                ...prev.triggerSourceData,
                ...partialData.triggerSourceData,
            }
        }));
    };

    return (
        <>
            <RowStack className={classes.add_trigger_dialog_header}>
                <span>Trigger Settings</span>
            </RowStack>

            <Stack className={classes.add_trigger_dialog_body}>
                <div className={classes.section_title}>Trigger Sources</div>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Select which sources can trigger this flow:
                </Typography>
                <Stack>
                    {availableTriggerSources.map((source) => {
                        const IconComponent = source.icon;
                        return (
                            <Box key={source.value} sx={{ mb: 1.5, background: '#f5f5f5', borderRadius: '8px', p: 1.5 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isTriggerSourceChecked(source.value)}
                                            onChange={() => handleTriggerSourceChange(source.value)}
                                            color="primary"
                                            sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                                        />
                                    }
                                    label={
                                        <RowStack spacing={1}>
                                            <IconComponent fontSize="small" />
                                            <Typography variant="body2" fontWeight="medium">
                                                {source.label}
                                            </Typography>
                                        </RowStack>
                                    }
                                />
                                <Typography variant="caption" sx={{ ml: 4, color: '#666666', display: 'block' }}>
                                    {source.description}
                                </Typography>
                                
                                {/* Source-specific configuration */}
                                {isTriggerSourceChecked(source.value) && source.requiresConfiguration && (
                                    <Box sx={{ mt: 2, ml: 4, mr: 1 }}>
                                        <TriggerSourceConfig
                                            source={source} 
                                            triggerData={localTriggerState}
                                            onUpdateTriggerData={handleUpdateTriggerData}
                                        />
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                    {availableTriggerSources.length === 0 && (
                        <Typography variant="caption" sx={{ color: '#666666', fontStyle: 'italic', mt: 1 }}>
                             No trigger sources available for your role.
                        </Typography>
                    )}
                </Stack>

                <Divider sx={{ my: 2 }} />

                <div className={classes.section_title}>Contact List Type</div>
                <RadioGroup
                    value={localTriggerState.triggerType}
                    onChange={handleTriggerTypeChange}
                    sx={{ marginBottom: 2 }}
                >
                    <FormControlLabel
                        value="blockList"
                        control={<Radio />}
                        label={
                            <Typography variant="body2">
                                Block List - Listed contacts cannot trigger the flow
                            </Typography>
                        }
                    />
                    <FormControlLabel
                        value="allowList"
                        control={<Radio />}
                        label={
                            <Typography variant="body2">
                                Allow List - Only listed contacts can trigger the flow
                            </Typography>
                        }
                    />
                </RadioGroup>

                <div className={classes.section_title}>Contact List</div>

                <Stack sx={{ alignItems: 'center' }}>
                    <Stack direction="row" justifyContent="center" sx={{ marginTop: 2 }}>
                        <SearchBox
                            variant="outlined"
                            autoComplete="off"
                            value={searchTerm}
                            onChange={(e) => {
                                if (!e.target.value?.includes('+')) {
                                    setSearchTerm(e.target.value);
                                }
                            }}
                            placeholder="Enter name or number"
                            InputProps={{
                                endAdornment: allowToAdd && (
                                    <IconButton onClick={() => setPhoneNumberAsContact(searchTerm)}>
                                        <Add sx={{ fontSize: 16 }} />
                                    </IconButton>
                                ),
                            }}
                            fullWidth
                        />
                    </Stack>
                    {searchTerm !== '' && (
                        <div
                            className="nowheel"
                            style={{
                                zIndex: 1000,
                                backgroundColor: 'var(--default-background)',
                                marginTop: '8px',
                                width: '310px'
                            }}
                        >
                            <WaContactLazyLoadingVirtuoso
                                onClick={handleContactSelect}
                                height={175}
                                searchFilter={searchTerm}
                            />
                        </div>
                    )}
                </Stack>

                <Stack spacing={1} sx={{ alignItems: 'center' }}>
                    {localTriggerState.contacts?.map((contactId) => (
                        <RowStack key={contactId} sx={{ height: '40px' }}>
                            <WhatsappIdCell
                                cellValue={contactId}
                                taskId={id?.substring(0, 24) || ''}
                                columnId={id?.substring(0, 24) || ''}
                                openPicker={() => { }}
                                onUpdate={() => { }}
                                togglePicker={() => { }}
                                closePicker={() => { }}
                                withoutPlus={true}
                            />
                            <IconButton onClick={() => handleRemoveContact(contactId)}>
                                <Close />
                            </IconButton>
                        </RowStack>
                    ))}
                </Stack>

                <Divider sx={{ my: 2 }} />

                <RowStack sx={{ gap: '8px', justifyContent: 'flex-end' }}>
                    <BTbotbutton
                        styleType="empty"
                        onClick={onClose}
                        size="medium"
                    >
                        Cancel
                    </BTbotbutton>
                    <SaveButton
                        onBeforeSave={saveTriggerData}
                        onAfterSave={onClose}
                        shouldClose={true}
                        disableBigLoader={true} />
                </RowStack>
            </Stack>
        </>
    );
} 